/** Viewport (NES-style) for Pixel Past rescue platformer. */
export const PIXEL_PAST_RESCUE_LOGICAL_WIDTH = 320;
export const PIXEL_PAST_RESCUE_LOGICAL_HEIGHT = 240;

export const PIXEL_PAST_RESCUE_WORLD_WIDTH = 320;
export const PIXEL_PAST_RESCUE_WORLD_HEIGHT = 640;

export const PIXEL_PAST_RESCUE_JUMP_SOUND_SRC = '/minigames/jump.mp3';
export const PIXEL_PAST_RESCUE_DIE_SOUND_SRC = '/minigames/die.mp3';

/** Server `minigames.pixelPastRescue.winPickValue` */
export const PIXEL_PAST_RESCUE_WIN_PICK_VALUE = 1;
/** Server `minigames.pixelPastRescue.lossPickValue` */
export const PIXEL_PAST_RESCUE_LOSS_PICK_VALUE = 0;
/** Server `minigames.pixelPastRescue.maxLives` */
export const PIXEL_PAST_RESCUE_MAX_LIVES = 3;

/** Upward deceleration while rising (keeps jump height readable). */
export const PIXEL_PAST_RESCUE_RISE_GRAVITY = 0.48;
/** Downward acceleration while falling — matches |jump| so descent feels as snappy as the hop. */
export const PIXEL_PAST_RESCUE_FALL_GRAVITY = 9.8;
export const PIXEL_PAST_RESCUE_JUMP_VELOCITY = -9.8;
/** Target run speed on ice (reached gradually). */
export const PIXEL_PAST_RESCUE_ICE_MAX_SPEED = 4.2;
/** Horizontal acceleration while holding a move key. */
export const PIXEL_PAST_RESCUE_ICE_ACCEL = 0.52;
/** Per-frame multiplier on ground when not pressing move (higher = more slide). */
export const PIXEL_PAST_RESCUE_ICE_GROUND_FRICTION = 0.968;
/** Slight drift in the air. */
export const PIXEL_PAST_RESCUE_ICE_AIR_FRICTION = 0.998;
export const PIXEL_PAST_RESCUE_MAX_FALL_SPEED = 9.8;
/** Radians advanced per frame during a jump flip (full turn ≈ 22 frames). */
export const PIXEL_PAST_RESCUE_JUMP_FLIP_SPEED = Math.PI / 11;
export const PIXEL_PAST_RESCUE_PLAYER_WIDTH = 14;
export const PIXEL_PAST_RESCUE_PLAYER_HEIGHT = 16;

/**
 * @typedef {{ x: number, y: number, w: number, h: number }} PixelPastRect
 * @typedef {{ x: number, y: number, w: number, h: number, minX: number, maxX: number, vx: number }} PixelPastMonster
 */

/** @type {PixelPastRect[]} */
export const PIXEL_PAST_RESCUE_PLATFORMS = Object.freeze([
  { x: 0, y: 608, w: 320, h: 32 },
  { x: 16, y: 518, w: 88, h: 14 },
  { x: 216, y: 518, w: 88, h: 14 },
  { x: 24, y: 428, w: 96, h: 14 },
  { x: 200, y: 428, w: 96, h: 14 },
  { x: 112, y: 338, w: 96, h: 14 },
  { x: 20, y: 248, w: 72, h: 14 },
  { x: 228, y: 248, w: 72, h: 14 },
  { x: 72, y: 158, w: 176, h: 14 },
  { x: 22, y: 136, w: 40, h: 14 },
  { x: 118, y: 114, w: 44, h: 14 },
  { x: 26, y: 92, w: 38, h: 14 },
  { x: 256, y: 92, w: 38, h: 14 },
  { x: 122, y: 70, w: 42, h: 14 },
  { x: 108, y: 54, w: 104, h: 14 },
]);

/** Static blocks the hero must jump over (not monsters). */
/** @type {PixelPastRect[]} */
export const PIXEL_PAST_RESCUE_OBSTACLES = Object.freeze([
  { x: 128, y: 594, w: 22, h: 14 },
  { x: 96, y: 324, w: 36, h: 14 },
  { x: 188, y: 324, w: 28, h: 14 },
]);

/**
 * @type {PixelPastMonster[]}
 * Touching any monster is a loss.
 */
export const PIXEL_PAST_RESCUE_MONSTERS = Object.freeze([
  { x: 200, y: 592, w: 16, h: 16, minX: 160, maxX: 268, vx: 1.4 },
  { x: 40, y: 412, w: 16, h: 16, minX: 24, maxX: 104, vx: -1.3 },
  { x: 148, y: 322, w: 16, h: 16, minX: 116, maxX: 188, vx: 1.35 },
  { x: 220, y: 232, w: 16, h: 16, minX: 228, maxX: 292, vx: 1.2 },
  { x: 130, y: 142, w: 16, h: 16, minX: 72, maxX: 232, vx: -1.5 },
  { x: 132, y: 54, w: 16, h: 16, minX: 124, maxX: 150, vx: 1.2 },
]);

/** Princess goal — same hit box as the hero (14×16), on the top terrace. */
export const PIXEL_PAST_RESCUE_GOAL = Object.freeze({
  x: 153,
  y: 38,
  w: PIXEL_PAST_RESCUE_PLAYER_WIDTH,
  h: PIXEL_PAST_RESCUE_PLAYER_HEIGHT,
});

export const PIXEL_PAST_RESCUE_PLAYER_SPAWN = Object.freeze({
  x: 40,
  y: 592,
  w: PIXEL_PAST_RESCUE_PLAYER_WIDTH,
  h: PIXEL_PAST_RESCUE_PLAYER_HEIGHT,
});

/**
 * @param {PixelPastRect} a
 * @param {PixelPastRect} b
 * @returns {boolean}
 */
export function pixelPastRectsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * @param {number} cameraY
 * @returns {number}
 */
export function clampPixelPastCameraY(cameraY) {
  const maxY = Math.max(0, PIXEL_PAST_RESCUE_WORLD_HEIGHT - PIXEL_PAST_RESCUE_LOGICAL_HEIGHT);
  return Math.max(0, Math.min(maxY, cameraY));
}

/**
 * @param {number} playerCenterY
 * @returns {number}
 */
export function computePixelPastCameraY(playerCenterY) {
  const target = playerCenterY - PIXEL_PAST_RESCUE_LOGICAL_HEIGHT * 0.55;
  return clampPixelPastCameraY(target);
}
